// Legacy audits index a handful of destinations. A numeric key above this is
// a brand code, so it must not size a sparse array.
const MAX_LEGACY_INDEX = 999

export const DESTINATION_URLS_FIELD = 'destination_urls'

export const isDestinationUrlsField = (fieldName?: string) =>
  fieldName === DESTINATION_URLS_FIELD ||
  Boolean(fieldName?.startsWith(`${DESTINATION_URLS_FIELD}.`))

const parseAuditValue = (value: unknown) => {
  if (value == null || value === '') {
    return null
  }

  if (typeof value !== 'string') {
    return value
  }

  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

const reconstructDestinationUrlsValue = (
  details: AuditLogItemChangeDetail[],
  key: 'previous_value' | 'new_value'
) => {
  const destinations = new Map<string, Record<string, unknown>>()
  // Several brandless URLs share the literal key `(none)`. Count each field
  // path so the second url stays a second row instead of overwriting the first.
  const brandlessSlot = new Map<string, number>()

  details.forEach(detail => {
    const fieldName = String(detail.field_name)

    if (fieldName === DESTINATION_URLS_FIELD) {
      return
    }

    const rest = fieldName.slice(DESTINATION_URLS_FIELD.length + 1)
    // Current audits use brand keys (including `(none)`); older ones use
    // array indexes. Split only a known leaf so dotted brand codes survive.
    const leaf = /^(.*)\.(url|brand|publisher_id)$/.exec(rest)
    const rawKey = leaf ? leaf[1] : rest
    const prop = leaf?.[2]
    const brandlessCount = brandlessSlot.get(fieldName) ?? 0
    if (rawKey === '(none)') brandlessSlot.set(fieldName, brandlessCount + 1)
    const destinationKey =
      rawKey === '(none)' && brandlessCount > 0
        ? `(none):${brandlessCount}`
        : rawKey
    const isIndex = /^\d+$/.test(destinationKey)
    const isBrandless =
      destinationKey === '(none)' || destinationKey.startsWith('(none):')

    const parsed = parseAuditValue(detail[key])

    if (parsed == null) {
      return
    }

    if (!prop) {
      if (typeof parsed === 'object' && !Array.isArray(parsed)) {
        destinations.set(destinationKey, {
          ...destinations.get(destinationKey),
          ...(parsed as Record<string, unknown>)
        })
      }

      return
    }

    destinations.set(destinationKey, {
      ...(!isIndex && !isBrandless ? { brand: destinationKey } : {}),
      ...destinations.get(destinationKey),
      [prop]: parsed
    })
  })

  if (!destinations.size) {
    return null
  }

  const keys = [...destinations.keys()]
  const usesIndexes =
    keys.every(key => /^\d+$/.test(key)) &&
    Math.max(...keys.map(Number)) <= MAX_LEGACY_INDEX
  if (usesIndexes) {
    const list = Array.from(
      { length: Math.max(...keys.map(Number)) + 1 },
      (_, index) => destinations.get(String(index)) ?? null
    )
    return JSON.stringify(list)
  }

  return JSON.stringify([...destinations.values()])
}

export const collapseDestinationUrlDetails = (
  details: AuditLogItemChangeDetail[] = []
): AuditLogItemChangeDetail[] => {
  const destinationDetails = details.filter(detail =>
    isDestinationUrlsField(String(detail.field_name))
  )

  if (destinationDetails.length === 0) {
    return details
  }

  const otherDetails = details.filter(
    detail => !isDestinationUrlsField(String(detail.field_name))
  )
  const canonicalDetail = destinationDetails.find(
    detail => detail.field_name === DESTINATION_URLS_FIELD
  )

  if (canonicalDetail) {
    return destinationDetails.length === 1
      ? details
      : [...otherDetails, canonicalDetail]
  }

  return [
    ...otherDetails,
    {
      ...destinationDetails[0],
      field_name: DESTINATION_URLS_FIELD,
      data_type: 'object',
      previous_value:
        reconstructDestinationUrlsValue(destinationDetails, 'previous_value') ??
        'null',
      new_value:
        reconstructDestinationUrlsValue(destinationDetails, 'new_value') ??
        'null'
    }
  ]
}

const isDestinationCreate = (change: FlattenChange) =>
  change.entity === 'campaign_destination_url' &&
  change.field_name === 'payload_create'

export const omitRedundantDestinationCreates = (changes: FlattenChanges) => {
  const hasSnapshot = changes.some(change =>
    isDestinationUrlsField(change.field_name)
  )
  let seenCreate = false

  return changes.filter(change => {
    if (!isDestinationCreate(change)) {
      return true
    }

    if (hasSnapshot) {
      return false
    }

    if (seenCreate) {
      return false
    }

    seenCreate = true
    return true
  })
}
